const SCROLL_EPS = 2;

class ProductSliderElement extends HTMLElement {
  #abortController;
  #resizeObserver;

  /** @param {HTMLElement} viewport */
  #getSlides(viewport) {
    return [...viewport.querySelectorAll(".carousel__slide")];
  }

  /** @param {HTMLElement} viewport */
  #maxScroll(viewport) {
    return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  }

  /**
   * @param {HTMLElement} viewport
   * @param {HTMLElement[]} slides
   */
  #scrollIndex(viewport, slides) {
    if (slides.length === 0) return 0;
    const left = viewport.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < slides.length; i++) {
      const dist = Math.abs(left - slides[i].offsetLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollToIndex(slides, index, behavior) {
    slides[index]?.scrollIntoView({ behavior, inline: "start", block: "nearest" });
  }

  /**
   * @param {HTMLElement} viewport
   * @param {HTMLButtonElement | null} prevBtn
   * @param {HTMLButtonElement | null} nextBtn
   */
  #syncArrows(viewport, prevBtn, nextBtn) {
    const maxScroll = this.#maxScroll(viewport);
    const left = viewport.scrollLeft;
    prevBtn?.toggleAttribute("disabled", left <= SCROLL_EPS);
    nextBtn?.toggleAttribute("disabled", left >= maxScroll - SCROLL_EPS);
  }

  connectedCallback() {
    const viewportNode = this.querySelector(".carousel__viewport");
    if (!viewportNode) return;

    this.disconnectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const carouselId = this.getAttribute("data-carousel-id") || "";
    const prevBtn = this.querySelector(`[data-carousel-prev="${carouselId}"]`);
    const nextBtn = this.querySelector(`[data-carousel-next="${carouselId}"]`);

    const syncArrows = () => {
      requestAnimationFrame(() => this.#syncArrows(viewportNode, prevBtn, nextBtn));
    };

    prevBtn?.addEventListener(
      "click",
      () => {
        const slides = this.#getSlides(viewportNode);
        const i = this.#scrollIndex(viewportNode, slides);
        if (i > 0) this.#scrollToIndex(slides, i - 1, "smooth");
      },
      { signal }
    );

    nextBtn?.addEventListener(
      "click",
      () => {
        const slides = this.#getSlides(viewportNode);
        const i = this.#scrollIndex(viewportNode, slides);
        if (i < slides.length - 1) this.#scrollToIndex(slides, i + 1, "smooth");
      },
      { signal }
    );

    viewportNode.addEventListener("scroll", syncArrows, { signal, passive: true });
    viewportNode.addEventListener("scrollend", syncArrows, { signal, passive: true });

    this.#resizeObserver = new ResizeObserver(() => syncArrows());
    this.#resizeObserver.observe(viewportNode);

    syncArrows();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#abortController?.abort();
    this.#abortController = undefined;
  }
}

if (!customElements.get("product-slider")) {
  customElements.define("product-slider", ProductSliderElement);
}
